/**
 * One tenant: who they are, what they pay, how they were onboarded, and what
 * they have built.
 *
 * "View as agency" opens a **read-only** support session: the admin sees the
 * agency's own app, every write is refused by the API, and the session is
 * written to the impersonation log before the token is issued.
 */
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import DeleteAgencyModal from '../components/DeleteAgencyModal';
import {
  ArrowLeft,
  Handshake,
  House,
  MapPin,
  PlayCircle,
  Prohibit,
  UserCircle,
  UsersThree,
  UserSwitch,
  CalendarCheck,
  Trash,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { useAgency, useImpersonate, useSetAgencyStatus } from '../queries/platform';
import { apiError } from '../lib/api';
import { formatDate, relativeDate } from '../lib/format';
import {
  Avatar,
  Badge,
  DefinitionList,
  ErrorState,
  LoadingBlock,
  StatusDot,
} from '../components/ui';
import { agencyState } from './Agencies';
import SubscriptionPanel from '../components/SubscriptionPanel';
import { Modal, Field, SubmitButton } from '../components/ui';

export default function AgencyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isPending, isError, error, refetch } = useAgency(id);
  const setStatus = useSetAgencyStatus();
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  if (isPending) return <LoadingBlock label="Loading agency…" />;
  if (isError) return <ErrorState message={apiError(error)} onRetry={() => void refetch()} />;

  const { agency, usage, nextNotice } = data;
  const state = agencyState(agency);
  const suspended = agency.status === 'inactive';
  const onboardedBy =
    agency.onboardedBy && typeof agency.onboardedBy === 'object' ? agency.onboardedBy : null;
  const registration =
    agency.registration && typeof agency.registration === 'object' ? agency.registration : null;

  const toggle = async () => {
    setActionError('');
    setNotice('');
    try {
      const next = suspended ? 'active' : 'inactive';
      await setStatus.mutateAsync({ id: agency._id, status: next });
      setNotice(next === 'inactive' ? 'Agency suspended.' : 'Agency reinstated.');
    } catch (err) {
      setActionError(apiError(err));
    }
  };

  return (
    <div className="stack animate-up" style={{ gap: 18 }}>
      <button
        className="btn btn-sm"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => navigate('/agencies')}
      >
        <ArrowLeft size={14} /> All agencies
      </button>

      {(notice || actionError) && (
        <div
          className="preview-note"
          style={{
            borderStyle: 'solid',
            borderColor: actionError ? 'var(--bad-line)' : 'var(--ok-line)',
            background: actionError ? 'var(--bad-bg)' : 'var(--ok-bg)',
            color: actionError ? 'var(--bad)' : 'var(--ok)',
            marginBottom: 0,
          }}
        >
          <div>
            <div>{actionError || notice}</div>
          </div>
        </div>
      )}

      <div className="card card-pad">
        <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
          <Avatar name={agency.name} size={54} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 21 }}>{agency.name}</h2>
            <div className="row row-tight" style={{ marginTop: 6, flexWrap: 'wrap' }}>
              <StatusDot tone={state.tone} />
              <span className="t-sm strong">{state.label}</span>
              {agency.address && (
                <>
                  <span className="faint">·</span>
                  <MapPin size={13} color="var(--mute)" />
                  <span className="t-sm muted">{agency.address}</span>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setImpersonating(true)}>
              <UserSwitch size={15} /> View as agency
            </button>
            <button
              className={`btn ${suspended ? 'btn-ok' : 'btn-bad'}`}
              onClick={toggle}
              disabled={setStatus.isPending}
            >
              {suspended ? <PlayCircle size={15} /> : <Prohibit size={15} />}
              {suspended ? 'Activate' : 'Suspend'}
            </button>
            {/* Last in the row on purpose: suspending is the reversible thing
                you almost always want, so it should be the easier reach.

                There is deliberately no role check. It was owner-only, but an
                account created through the console defaults to `admin`, so the
                restriction mostly hid the action from the people who needed it.
                What stops an accident is the typed agency name and the required
                reason in the dialog, not who is signed in. */}
            <button className="btn btn-bad" onClick={() => setDeleting(true)}>
              <Trash size={15} /> Delete
            </button>
          </div>
        </div>

      </div>

      <SubscriptionPanel agency={agency} nextNotice={nextNotice} />

      <div className="grid grid-3" style={{ gap: 18 }}>
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">Contact</h2>
          </div>
          <div className="card-pad">
            <DefinitionList
              rows={[
                ['Contact', agency.contactName],
                ['Mobile', agency.phone],
                ['Email', agency.email],
                ['Website', agency.website],
                ['Address', agency.address],
              ]}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="card-title">Onboarding</h2>
          </div>
          <div className="card-pad">
            <DefinitionList
              rows={[
                ['Created', formatDate(agency.createdAt)],
                [
                  'Activated',
                  agency.activatedAt ? (
                    formatDate(agency.activatedAt)
                  ) : (
                    <Badge tone="info">Not yet</Badge>
                  ),
                ],
                ['Onboarded by', onboardedBy?.name],
                [
                  'From registration',
                  registration ? (
                    <Link to="/registrations">{relativeDate(registration.createdAt)}</Link>
                  ) : (
                    'Direct onboarding'
                  ),
                ],
              ]}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="card-title">Workspace</h2>
            <p className="card-sub">What they&apos;ve built</p>
          </div>
          <div className="card-pad stack" style={{ gap: 13 }}>
            <UsageRow icon={House} label="Properties" value={usage.properties} />
            <UsageRow icon={UsersThree} label="Clients" value={usage.customers} />
            <UsageRow icon={UserCircle} label="Agents" value={usage.agents} />
            <UsageRow icon={CalendarCheck} label="Visits" value={usage.visits} />
            <UsageRow icon={Handshake} label="Deals" value={usage.deals} />
          </div>
        </div>
      </div>

      {deleting && (
        <DeleteAgencyModal
          agency={agency}
          onClose={() => setDeleting(false)}
          onDeleted={(summary) => {
            setDeleting(false);
            // Back to the directory: this page's record no longer exists, and
            // leaving it up would show a workspace that has just been removed.
            navigate('/agencies', { state: { notice: summary } });
          }}
        />
      )}

      {impersonating && (
        <ImpersonateModal
          agencyId={agency._id}
          agencyName={agency.name}
          onClose={() => setImpersonating(false)}
        />
      )}
    </div>
  );
}

/**
 * Opening a support session is a two-step: the reason is recorded in the audit
 * log, and the read-only nature is stated before anyone clicks through.
 */
function ImpersonateModal({
  agencyId,
  agencyName,
  onClose,
}: {
  agencyId: string;
  agencyName: string;
  onClose: () => void;
}) {
  const impersonate = useImpersonate();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const start = async () => {
    setError('');
    try {
      const session = await impersonate.mutateAsync({ id: agencyId, reason: reason.trim() });
      // A new tab: the console session stays exactly as it was, so the admin is
      // never left wondering which identity they are in.
      window.open(session.url, '_blank', 'noopener');
      onClose();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <Modal
      title={`View ${agencyName} as support`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={impersonate.isPending}>
            Cancel
          </button>
          <SubmitButton
            pending={impersonate.isPending}
            pendingLabel="Opening…"
            className="btn btn-primary"
            onClick={start}
          >
            Open their app
          </SubmitButton>
        </>
      }
    >
      <p className="t-sm muted" style={{ margin: '0 0 14px', lineHeight: 1.6 }}>
        Opens their workspace in a new tab, exactly as they see it. The session is{' '}
        <strong style={{ color: 'var(--ink)' }}>read-only</strong>. Nothing can be created,
        edited or deleted. It expires in an hour, and is recorded against your name.
      </p>
      <Field label="Reason" hint="Recorded in the impersonation log. A ticket number is ideal.">
        <input
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. ticket #4821, deal totals look wrong"
        />
      </Field>
      {error && <p className="field-error">{error}</p>}
    </Modal>
  );
}

function UsageRow({ icon: IconCmp, label, value }: { icon: Icon; label: string; value: number }) {
  return (
    <div className="row">
      <div className="icon-tile" style={{ width: 32, height: 32 }}>
        <IconCmp size={16} />
      </div>
      <span className="t-md">{label}</span>
      <div className="spacer" />
      <span className="num" style={{ fontSize: 17 }}>
        {value.toLocaleString('en-IN')}
      </span>
    </div>
  );
}
